import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import { ApiError, MessageService, FormsUtilsService } from '../../../../core';
import { PessoaEndereco } from '../pessoa-endereco';
import { Cep } from '../../../../core/services/cep';
import { Cidade, CidadeSubRegiao, CidadeSubRegiaoBairro, CidadeSearchesHttpService } from '../../../cidade';
import { RegiaoCidadeSubRegiaoHttpService } from '../../../regiao/cidade/sub-regiao/services';
import { Pais, PaisSearchesHttpService } from '../../../pais';
import { LeadEndereco } from 'app/home/crm/lead/endereco';

@Component({
    selector: 'sgt-pessoa-endereco-cidade',
    templateUrl: './pessoa-endereco-cidade.component.html',
    styleUrls: ['./pessoa-endereco-cidade.component.less']
})
export class PessoaEnderecoCidadeComponent implements OnChanges {
    /** Se pode editar os dados do form */
    @Input() canEdit = true;
    @Input() tipoEndereco: number;
    @Input() entity: PessoaEndereco | LeadEndereco;
    @Input() idRepresentante: number;
    @Input() exterior = false; // Se for um endereço de uma pessoa do exterior
    @Input() isCliente: boolean; // Se é o cadastro de cliente
    @Input() canSave = true; // Se pode salvar as informações da cidade
    @Input() cidadeRequired = true;
    @Input() paisRequired = true;
    @Output() cidadeSelected = new EventEmitter<Cidade>();
    @Output() subRegiaoChanged = new EventEmitter<number>();

    // ufMascaraIe = ''; // UF da máscara da IE
    subRegioes: CidadeSubRegiao[];
    bairros: CidadeSubRegiaoBairro[];

    @ViewChild('form') form: NgForm;

    constructor(private messageService: MessageService,
                private formsUtilsService: FormsUtilsService,
                private regiaoCidadeSubRegiaoHttpService: RegiaoCidadeSubRegiaoHttpService,
                private cidadeSearchesHttpService: CidadeSearchesHttpService,
                private paisSearchesHttpService: PaisSearchesHttpService) { }

    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            // Quando existir o endereço
            if (propName === 'entity') {
                if (changes[propName].isFirstChange()) {
                    if (this.entity) {
                        this.carregarSubRegioes();

                        // Setando a UF da máscara da IE
                        // if (this.entity.cidade) {
                        //     this.ufMascaraIe = this.entity.cidade.estado.uf;
                        // }
                    }
                }
            } else if (propName === 'idRepresentante') { // Ao alterar o representante
                if (!changes[propName].isFirstChange()) {
                    if (this.entity) {
                        // Removendo a sub-região e bairro da cidade caso estejam informadas e carregando as sub-regiões do representante
                        this.clearSubRegiao();
                        this.carregarSubRegioes();
                    }
                }
            } else if (propName === 'exterior') { // Ao alterar a opção de exterior
                if (!changes[propName].isFirstChange()) {
                    if (this.entity) {
                        // Setando a cidade e país conforme o valor de se é do exterior ou não
                        this.setCidadeEPais();
                    }
                }
            }
        }
    }

    insert() {
        this.setCidadeEPais();
    }

    /** Seta os dados do CEP informado na cidade */
    setDadosCep(cep: Cep) {
        if (!this.entity) {
            return;
        }

        if (cep) {
            this.entity.idCidade = cep.cidade.id;
            this.entity.cidade = cep.cidade;
            this.carregarSubRegioes();
        } else {
            this.clearCidade();
        }
    }

    /** Ao selecionar a cidade */
    onSelectCidade(cidade: Cidade) {
        if (cidade) {
            this.entity.idCidade = cidade.id;
            // this.ufMascaraIe = cidade.estado.uf;
        } else {
            this.clearCidade();
        }
        this.clearSubRegiao();
        this.carregarSubRegioes();
        this.setPais(cidade?.estado?.pais ?? null);
        this.cidadeSelected.emit(cidade);
    }

    /** Seta o pais e cidade conforme se for um endereço de exterior ou não */
    private setCidadeEPais() {
        if (!this.entity) {
            return;
        }

        if (this.exterior) {
            // Setamos a cidade "Exterior" e setamos nos endereços
            this.setCidadeExterior();
            // Removemos o pais dos endereços
            this.clearPais();
        } else {
            // Removemos a cidade dos endereços
            this.clearCidade();
            // Setamos o pais Brasil
            this.setPaisBrasil();
        }
    }

    /** Seta a cidade "Exterior" */
    private setCidadeExterior() {
        this.cidadeSearchesHttpService.findExterior()
        .subscribe(
            cidadeExterior => {
                this.entity.idCidade = cidadeExterior.id;
                this.entity.cidade = cidadeExterior;
            },
            (error: ApiError) => {
                this.messageService.error('Ocorreu um erro ao tentar obter a cidade "Exterior". Selecione manualmente a cidade para continuar o cadastro', 'Erro ao tentar obter a cidade "Exterior"', error);
            }
        )
    }

    /** Remove a cidade */
    clearCidade() {
        this.entity.idCidade = null;
        this.entity.cidade = null;
        this.clearSubRegiao();
        this.setPais(null);
    }

    /** Verifica se deve exibir os campos de sub-região e bairros */
    isShowSubRegiaoEBairro(): boolean {
        // Somente quando for um cadastro de cliente e a cidade possuir sub-regiões
        return this.isCliente && this.entity?.cidade && this.entity?.cidade?.possuiSubRegioes;
    }

    /** Carrega as sub-regiões da cidade informada e representante */
    carregarSubRegioes() {
        this.subRegioes = [];
        if (!this.entity?.cidade) {
            return;
        }

        this.regiaoCidadeSubRegiaoHttpService.searchDisponiveisParaRepresentantes([this.idRepresentante], this.entity.cidade.id)
            .subscribe(
                entities => {
                    this.subRegioes = entities;
                    this.carregarBairrosDaSubRegiao();
                },
                (error: ApiError) => this.messageService.error(error.message, 'Erro ao pesquisar as sub-regiões e bairros da cidade', error)
            );
    }

    /** Ao alterar a sub-região no select */
    onChangeSubRegiao(id: number) {
        this.entity.idCidadeSubRegiao = id;
        this.entity.idCidadeSubRegiaoBairro = null;
        this.carregarBairrosDaSubRegiao();

        this.subRegiaoChanged.emit(id);
    }

    /** Remove a sub-região e o bairro da sub-região */
    clearSubRegiao() {
        this.entity.idCidadeSubRegiao = null;
        this.entity.idCidadeSubRegiaoBairro = null;
    }

    /** Carrega os bairros da sub-região informada */
    carregarBairrosDaSubRegiao() {
        this.bairros = [];
        if (!this.subRegioes) {
            return;
        }

        const subRegiaoAtual = this.subRegioes.find(subRegiao => subRegiao.id === this.entity?.idCidadeSubRegiao);
        if (subRegiaoAtual) {
            this.bairros = subRegiaoAtual.bairros;
        }
    }

    /** Ao alterar o pais */
    onChangePais(pais: Pais) {
        if (pais) {
            this.entity.idPais = pais.id;
        } else {
            this.entity.idPais = null;
        }
    }

    private setPais(pais: Pais) {
        this.entity.pais = pais;
        this.onChangePais(this.entity.pais);
    }

    /** Seta o pais Brasil */
    private setPaisBrasil() {
        this.paisSearchesHttpService.findBrasil()
            .subscribe(
                paisBrasil => {
                    this.entity.idPais = paisBrasil.id;
                    this.entity.pais = paisBrasil;
                },
                (error: ApiError) => {
                    this.messageService.error('Ocorreu um erro ao obter o país Brasil. Selecione manualmente o país para continuar o cadastro', 'Erro ao tentar obter o país Brasil', error)
                }
            );
    }


    /** Remove o pais */
    private clearPais() {
        this.entity.idPais = null;
        this.entity.pais = null;
    }

     /** Verifica se os dados da cidade estão válidos */
     isValid(): boolean {
        // Se não puder salvar então retoramos como form válido para não dar erros de campos inválidos
        if (!this.canSave) {
            return true;
        }

        this.formsUtilsService.makeAllFieldsDirty(this.form);
        if (!this.form.valid) {
            return false;
        }

        return true;
    }
}
